// Delay before expanding after swiper navigation (must be >= swiper speed: 600ms)
const CARD_OPEN_DELAY_MS = 650;
// Must match CSS transition duration on .cmp-teaser__exp-card-slider (desktop expand)
const CARD_EXPAND_MS = 650;
const CARD_COLLAPSE_MS = 650;
// Mirrors breakpoints.lg from shared-utils
const DESKTOP_BREAKPOINT = 1024;

const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;
const getCarouselRoot = (teaser: HTMLElement) =>
  teaser.closest<HTMLElement>('.cmp-editorial-carousel, .cmp-swiper-carousel');
const isElementVisible = (element: HTMLElement | null) => {
  if (!element) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return element.getClientRects().length > 0;
};
const shouldCloseFirstCardByDefault = (teaser: HTMLElement) =>
  getCarouselRoot(teaser)?.getAttribute('data-cmp-first-card-closed') === 'true';
const isSmoothExpandableCards = (teaser: HTMLElement) =>
  getCarouselRoot(teaser)?.classList.contains('-smooth-expandable-cards') ?? false;
const isExpandAnimationDisabled = (teaser: HTMLElement) =>
  getCarouselRoot(teaser)?.classList.contains('-disable-expand-animation') ?? false;
type SwiperLike = {
  destroyed?: boolean;
  slides?: ArrayLike<Element>;
  slideTo: (index: number, speed?: number, runCallbacks?: boolean) => void;
};

const isFirstCarouselSlide = (teaser: HTMLElement, carouselRoot: HTMLElement | null | undefined) =>
  Boolean(
    carouselRoot &&
      teaser.parentElement &&
      carouselRoot.querySelector('.swiper-slide') === teaser.parentElement
  );

const isModalHashHref = (anchor: HTMLAnchorElement): boolean => {
  const raw = anchor.getAttribute('href');
  if (!raw) return false;
  try {
    const resolved = new URL(raw, window.location.href);
    return resolved.hash.length > 1;
  } catch {
    return false;
  }
};

const alignExpandedCardToStart = (teaser: HTMLElement): void => {
  if (!isDesktop()) return;

  const slide = teaser.parentElement;
  if (!slide) return;

  const carouselRoot = getCarouselRoot(teaser);
  const swiper = (carouselRoot as HTMLElement & { swiper?: SwiperLike })?.swiper;
  const slideIndex = Array.from(swiper?.slides ?? []).indexOf(slide);
  if (swiper && !swiper.destroyed && slideIndex >= 0) {
    const swiperAny = swiper as SwiperLike & {
      params?: { slidesOffsetBefore?: number; freeMode?: { enabled?: boolean } };
      slides?: ArrayLike<Element>;
      translate?: number;
      minTranslate?: () => number;
      maxTranslate?: () => number;
      setTranslate?: (value: number) => void;
      updateProgress?: (translate?: number) => void;
      updateActiveIndex?: (newActiveIndex?: number) => void;
      updateSlidesClasses?: () => void;
    };
    const slideEl = swiperAny.slides?.[slideIndex] as HTMLElement | undefined;
    swiper.slideTo(slideIndex, 0, false);
    const offsetBefore = Number(swiperAny.params?.slidesOffsetBefore ?? 0);
    const shouldApplyManualTranslate =
      Boolean(swiperAny.params?.freeMode?.enabled) || (slideIndex === 0 && offsetBefore > 0);
    if (shouldApplyManualTranslate && slideEl && swiperAny.setTranslate) {
      const rawTargetTranslate = offsetBefore - slideEl.offsetLeft;
      const minTranslate = swiperAny.minTranslate?.() ?? 0;
      const maxTranslate = swiperAny.maxTranslate?.() ?? 0;
      const lowerBound = Math.min(minTranslate, maxTranslate);
      const upperBound = Math.max(minTranslate, maxTranslate);
      const clampedTargetTranslate = Math.min(upperBound, Math.max(lowerBound, rawTargetTranslate));
      swiperAny.setTranslate(clampedTargetTranslate);
      swiperAny.updateProgress?.(clampedTargetTranslate);
      swiperAny.updateActiveIndex?.();
      swiperAny.updateSlidesClasses?.();
    }
    return;
  }

  slide.scrollIntoView({ block: 'nearest', inline: 'start' });
};

const syncScrollableWidthForExperienceCards = (teaser: HTMLElement): void => {
  const carouselRoot = getCarouselRoot(teaser);
  if (!carouselRoot) return;
  const wrapper = carouselRoot.querySelector<HTMLElement>('.swiper-wrapper');
  const swiper = (carouselRoot as HTMLElement & { swiper?: SwiperLike })?.swiper as
    | (SwiperLike & {
        params?: { spaceBetween?: number };
        translate?: number;
        minTranslate?: () => number;
        maxTranslate?: () => number;
        update?: () => void;
        setTranslate?: (value: number) => void;
        activeIndex?: number;
      })
    | undefined;
  if (!wrapper || !swiper || swiper.destroyed) return;

  const slides = Array.from(wrapper.querySelectorAll<HTMLElement>('.swiper-slide'));
  const spaceBetween = Number(swiper.params?.spaceBetween ?? 0);

  const collapsedWidth = teaser.offsetWidth;
  const totalSlidesWidth =
    slides.reduce((sum, currentSlide) => {
      const cardEl = currentSlide.querySelector<HTMLElement>('.cmp-teaser__exp-card-slider');
      const isClosing = cardEl?.classList.contains('is-closing');
      const w = isClosing ? collapsedWidth : currentSlide.offsetWidth;
      return sum + w;
    }, 0) +
    Math.max(0, slides.length - 1) * spaceBetween;

  wrapper.style.width = `${Math.ceil(totalSlidesWidth)}px`;

  requestAnimationFrame(() => {
    if (!wrapper.isConnected || !swiper || swiper.destroyed) return;
    swiper.update?.();
    const currentTranslate = swiper.translate ?? 0;
    const minTranslate = swiper.minTranslate?.() ?? 0;
    const maxTranslate = swiper.maxTranslate?.() ?? 0;
    const clampedTranslate = Math.max(maxTranslate, Math.min(minTranslate, currentTranslate));
    if (swiper.setTranslate && clampedTranslate !== currentTranslate) {
      swiper.setTranslate(clampedTranslate);
    }
  });
};

type ExpandableSwiper = SwiperLike & {
  params?: { slidesOffsetAfter?: number; speed?: number };
  width?: number;
  activeIndex?: number;
  animating?: boolean;
  update?: () => void;
};

const alignActiveCardOnMobile = (teaser: HTMLElement): void => {
  if (isDesktop()) return;

  const carouselRoot = getCarouselRoot(teaser) as
    | (HTMLElement & { swiper?: ExpandableSwiper })
    | null;
  if (!carouselRoot?.classList.contains('-smooth-expandable-cards')) return;

  const swiper = carouselRoot.swiper;
  if (!swiper || swiper.destroyed || !swiper.params) return;

  const slides = carouselRoot.querySelectorAll<HTMLElement>('.swiper-slide');
  const viewport = swiper.width ?? carouselRoot.clientWidth;
  swiper.params.slidesOffsetAfter = Math.max(
    0,
    Math.ceil(viewport - (slides[slides.length - 1]?.offsetWidth ?? 0))
  );

  requestAnimationFrame(() => {
    if (!carouselRoot.isConnected || swiper.destroyed) return;
    swiper.update?.();
    swiper.slideTo(
      swiper.activeIndex ?? 0,
      swiper.animating ? swiper.params?.speed ?? 0 : 0,
      false
    );
  });
};

const addEventListenerToButton = (teaser: HTMLElement) => {
  if (teaser.classList.contains('cmp-teaser__exp-card-slider')) {
    const carousel = teaser.closest<HTMLElement>('.cmp-editorial-carousel, .cmp-swiper-carousel');
    const isFreeScrollMode = () => carousel?.getAttribute('data-cmp-is-progressbar') === 'true';
    let pendingExpand = false;
    let closeTimer: ReturnType<typeof setTimeout> | null = null;
    let expandSettledFallback: ReturnType<typeof setTimeout> | null = null;
    let expandTransitionEndHandler: ((event: TransitionEvent) => void) | null = null;
    let expandWidthAlignRafId: number | null = null;
    let collapseSettledFallback: ReturnType<typeof setTimeout> | null = null;
    let collapseTransitionEndHandler: ((event: TransitionEvent) => void) | null = null;
    let collapseClassRemovalTimer: ReturnType<typeof setTimeout> | null = null;
    const skipExpandAnimation = isExpandAnimationDisabled(teaser);
    const shouldAutoExpandFirstCard = isSmoothExpandableCards(teaser)
      ? true
      : !shouldCloseFirstCardByDefault(teaser);
    let hasAppliedInitialDesktopState = false;
    const closeButton = teaser.querySelector<HTMLElement>('.cmp-teaser__close-button');
    const canCloseCard = () => isElementVisible(closeButton);

    const stopExpandWidthAlignLoop = () => {
      if (expandWidthAlignRafId != null) {
        cancelAnimationFrame(expandWidthAlignRafId);
        expandWidthAlignRafId = null;
      }
    };

    const clearExpandAnimation = () => {
      stopExpandWidthAlignLoop();
      if (expandTransitionEndHandler) {
        teaser.removeEventListener('transitionend', expandTransitionEndHandler);
        expandTransitionEndHandler = null;
      }
      if (expandSettledFallback) {
        clearTimeout(expandSettledFallback);
        expandSettledFallback = null;
      }
    };

    const clearCollapseAnimation = () => {
      if (collapseTransitionEndHandler) {
        teaser.removeEventListener('transitionend', collapseTransitionEndHandler);
        collapseTransitionEndHandler = null;
      }
      if (collapseSettledFallback) {
        clearTimeout(collapseSettledFallback);
        collapseSettledFallback = null;
      }
      if (collapseClassRemovalTimer) {
        clearTimeout(collapseClassRemovalTimer);
        collapseClassRemovalTimer = null;
      }
    };

    const addExpandedSettled = () => {
      if (teaser.classList.contains('expanded')) {
        teaser.classList.add('expanded-settled');
      }
    };

    /** Clean up slide inline styles added during expand animation. */
    const releaseSlideTransition = (slide: HTMLElement | null) => {
      if (!slide) return;
      slide.style.removeProperty('transition');
      slide.style.removeProperty('width');
    };

    const collapseCardWithAnimation = (card: HTMLElement) => {
      if (!card.classList.contains('expanded') || card.classList.contains('is-closing')) return;

      if (isExpandAnimationDisabled(card)) {
        card.classList.remove('expanded-settled', 'expanded');
        return;
      }

      card.classList.add('is-closing');
      requestAnimationFrame(() => {
        card.classList.remove('expanded-settled', 'expanded');
      });

      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.target !== card || e.propertyName !== 'width') return;
        card.removeEventListener('transitionend', onTransitionEnd);
        card.classList.remove('is-closing');
      };
      card.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(() => {
        card.removeEventListener('transitionend', onTransitionEnd);
        card.classList.remove('is-closing');
      }, CARD_COLLAPSE_MS + 200);
    };

    const collapseOtherExperienceCards = () => {
      if (!carousel) return;
      carousel
        .querySelectorAll<HTMLElement>('.cmp-teaser__exp-card-slider.expanded')
        .forEach((card) => {
          if (card !== teaser) {
            collapseCardWithAnimation(card);
          }
        });
    };

    const expand = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      clearExpandAnimation();
      clearCollapseAnimation();
      collapseOtherExperienceCards();
      teaser.classList.remove('is-closing');
      teaser.classList.remove('expanded-settled');

      if (!isDesktop()) {
        teaser.classList.add('expanded', 'expanded-settled');
        return;
      }

      if (skipExpandAnimation) {
        teaser.classList.add('expanded');
        alignExpandedCardToStart(teaser);
        syncScrollableWidthForExperienceCards(teaser);
        addExpandedSettled();
        return;
      }

      const slide = teaser.parentElement;
      const currentWidth = teaser.offsetWidth;
      const swiperAny = (
        getCarouselRoot(teaser) as HTMLElement & {
          swiper?: SwiperLike & {
            destroyed?: boolean;
            params?: { slidesOffsetBefore?: number };
            setTranslate?: (value: number) => void;
            updateProgress?: (translate?: number) => void;
            minTranslate?: () => number;
            maxTranslate?: () => number;
          };
        }
      )?.swiper;

      if (slide && isFreeScrollMode()) {
        slide.style.setProperty('width', `${currentWidth}px`, 'important');
        void slide.offsetWidth;
        slide.style.setProperty('transition', `width ${CARD_EXPAND_MS}ms ease-out`, 'important');
      }

      const onExpandTransitionEnd = (event: TransitionEvent) => {
        if (event.target !== teaser || event.propertyName !== 'width') return;
        clearExpandAnimation();
        releaseSlideTransition(slide);
        alignExpandedCardToStart(teaser);
        syncScrollableWidthForExperienceCards(teaser);
        addExpandedSettled();
      };

      expandTransitionEndHandler = onExpandTransitionEnd;
      teaser.addEventListener('transitionend', onExpandTransitionEnd);
      expandSettledFallback = setTimeout(() => {
        if (!teaser.classList.contains('expanded')) return;
        const targetWidth = 840; // expanded width desktop
        if (Math.abs(teaser.offsetWidth - targetWidth) < 10) {
          clearExpandAnimation();
          releaseSlideTransition(slide);
          alignExpandedCardToStart(teaser);
          syncScrollableWidthForExperienceCards(teaser);
          addExpandedSettled();
        } else {
          clearExpandAnimation();
          expandSettledFallback = setTimeout(() => {
            releaseSlideTransition(slide);
            alignExpandedCardToStart(teaser);
            syncScrollableWidthForExperienceCards(teaser);
            addExpandedSettled();
          }, 400);
        }
      }, CARD_EXPAND_MS + 200);

      teaser.style.width = `${currentWidth}px`;
      void teaser.offsetWidth;
      teaser.classList.add('expanded');

      requestAnimationFrame(() => {
        teaser.style.removeProperty('width');
        if (!isFreeScrollMode()) {
          syncScrollableWidthForExperienceCards(teaser);
          alignExpandedCardToStart(teaser);
          return;
        }

        const closingCard = carousel?.querySelector<HTMLElement>(
          '.cmp-teaser__exp-card-slider.is-closing'
        );
        const closingSlide = closingCard?.parentElement;
        const collapsedWidth = closingCard ? teaser.offsetWidth : 0;
        const slideIndex = slide
          ? Array.from(carousel?.querySelectorAll<HTMLElement>('.swiper-slide') ?? []).indexOf(
              slide
            )
          : -1;
        const closingSlideIndex = closingSlide
          ? Array.from(carousel?.querySelectorAll<HTMLElement>('.swiper-slide') ?? []).indexOf(
              closingSlide
            )
          : -1;

        const runAlignLoop = () => {
          if (!teaser.classList.contains('expanded') || teaser.classList.contains('is-closing')) {
            expandWidthAlignRafId = null;
            return;
          }
          if (swiperAny && !swiperAny.destroyed && slide && swiperAny.setTranslate) {
            const offsetBefore = Number(swiperAny.params?.slidesOffsetBefore ?? 0);
            const closingSlideCurrentExtra =
              closingSlide && closingSlideIndex >= 0 && closingSlideIndex < slideIndex
                ? Math.max(0, closingSlide.offsetWidth - collapsedWidth)
                : 0;
            const rawTarget = offsetBefore - (slide.offsetLeft - closingSlideCurrentExtra);
            const min = swiperAny.minTranslate?.() ?? 0;
            const max = swiperAny.maxTranslate?.() ?? 0;
            const clamped = Math.max(max, Math.min(min, rawTarget));
            swiperAny.setTranslate(clamped);
            swiperAny.updateProgress?.(clamped);
          }
          expandWidthAlignRafId = requestAnimationFrame(runAlignLoop);
        };

        syncScrollableWidthForExperienceCards(teaser);
        expandWidthAlignRafId = requestAnimationFrame(runAlignLoop);
      });
    };

    const collapse = () => {
      if (teaser.classList.contains('is-closing')) return;

      clearExpandAnimation();
      clearCollapseAnimation();
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }

      releaseSlideTransition(teaser.parentElement);

      if (skipExpandAnimation) {
        teaser.classList.remove('expanded-settled', 'expanded', 'is-closing');
        syncScrollableWidthForExperienceCards(teaser);
        return;
      }

      teaser.classList.add('is-closing');

      const finishCollapse = () => {
        clearCollapseAnimation();
        teaser.classList.remove('is-closing');
        syncScrollableWidthForExperienceCards(teaser);
      };

      const onCollapseTransitionEnd = (event: TransitionEvent) => {
        if (event.target !== teaser || event.propertyName !== 'width') return;
        finishCollapse();
      };

      collapseTransitionEndHandler = onCollapseTransitionEnd;
      teaser.addEventListener('transitionend', onCollapseTransitionEnd);

      requestAnimationFrame(() => {
        teaser.classList.remove('expanded-settled', 'expanded');
      });

      collapseSettledFallback = setTimeout(finishCollapse, CARD_COLLAPSE_MS);
    };

    const openCard = () => {
      if (!isDesktop()) return;

      if (isFreeScrollMode()) {
        pendingExpand = false;
        if (!teaser.classList.contains('expanded')) {
          expand();
        }
        return;
      }

      const parent = teaser.parentElement;
      if (parent?.classList.contains('swiper-slide-active')) {
        if (!teaser.classList.contains('expanded')) {
          expand();
        }
      } else {
        pendingExpand = false;
      }
    };

    const applyInitialDesktopState = () => {
      if (!isDesktop() || hasAppliedInitialDesktopState) return;

      if (!shouldAutoExpandFirstCard) {
        teaser.classList.remove('expanded', 'expanded-settled', 'is-closing');
        hasAppliedInitialDesktopState = true;
        return;
      }

      if (!teaser.parentElement?.classList.contains('swiper-slide-active')) {
        if (shouldAutoExpandFirstCard && isFirstCarouselSlide(teaser, carousel)) {
          pendingExpand = true;
        } else {
          hasAppliedInitialDesktopState = true;
        }
        return;
      }

      expand();
      pendingExpand = false;
      hasAppliedInitialDesktopState = true;
    };

    if (!isDesktop()) {
      if (
        !isSmoothExpandableCards(teaser) ||
        teaser.parentElement?.classList.contains('swiper-slide-active')
      ) {
        teaser.classList.add('expanded', 'expanded-settled');
      }
      if (isSmoothExpandableCards(teaser)) {
        alignActiveCardOnMobile(teaser);
      }
    } else {
      applyInitialDesktopState();
    }

    window.addEventListener('resize', () => {
      if (!isDesktop()) {
        teaser.classList.remove('is-closing');
        if (
          !isSmoothExpandableCards(teaser) ||
          teaser.parentElement?.classList.contains('swiper-slide-active')
        ) {
          teaser.classList.add('expanded', 'expanded-settled');
        } else {
          teaser.classList.remove('expanded', 'expanded-settled');
        }
        if (isSmoothExpandableCards(teaser)) {
          alignActiveCardOnMobile(teaser);
        }
        return;
      }
      applyInitialDesktopState();
    });

    closeButton?.addEventListener('click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isDesktop()) return;
      if (!canCloseCard()) return;

      collapse();
    });

    teaser.addEventListener('click', (event: Event) => {
      const actionLink = (event.target as HTMLElement).closest('.cmp-teaser__action-link');
      const clickedCloseButton = (event.target as HTMLElement).closest('.cmp-teaser__close-button');
      if (clickedCloseButton) return;

      // Mobile: click only navigates (slideToClickedSlide handles it), no toggle
      if (!isDesktop()) return;

      const isExpanded = teaser.classList.contains('expanded');

      if (actionLink) {
        // On CTA click:
        // - closed card: open/expand (prevent navigation until expanded)
        // - expanded card: allow native navigation (modal opens from CTA href); collapse card for hash/modal links
        const anchor =
          actionLink instanceof HTMLAnchorElement
            ? actionLink
            : actionLink.closest<HTMLAnchorElement>('a');
        if (!isExpanded) {
          event.preventDefault();
          event.stopPropagation();
          openCard();
        } else if (anchor && isModalHashHref(anchor)) {
          collapse();
        }
        return;
      }

      // Click on the card (non-CTA):
      // - expanded card: prevent wrapper-link navigation / modal
      // - closed card: open/expand the card (no modal)
      event.preventDefault();
      event.stopPropagation();

      if (isExpanded) return;

      // Keep existing expansion rules for closed card.
      if (isFreeScrollMode()) {
        pendingExpand = false;
        expand();
        return;
      }

      const parent = teaser.parentElement;
      if (parent?.classList.contains('swiper-slide-active')) {
        expand();
      } else {
        // Non-active cards should not become active nor trigger repositioning on click.
        pendingExpand = false;
      }
    });

    if (teaser.parentElement) {
      const observer = new MutationObserver(() => {
        if (!isDesktop()) {
          teaser.classList.remove('is-closing');
          if (
            !isSmoothExpandableCards(teaser) ||
            teaser.parentElement?.classList.contains('swiper-slide-active')
          ) {
            teaser.classList.add('expanded', 'expanded-settled');
          } else {
            teaser.classList.remove('expanded', 'expanded-settled');
          }
          if (isSmoothExpandableCards(teaser)) {
            alignActiveCardOnMobile(teaser);
          }
          return;
        }

        // Always attempt to apply initial state so the first card opens correctly
        // regardless of scroll mode (fixes auto-open when closeFirstCard is unchecked).
        applyInitialDesktopState();

        // In free scroll mode the clicked card can stay expanded without being active.
        if (isFreeScrollMode()) {
          return;
        }

        const isActive = teaser.parentElement?.classList.contains('swiper-slide-active');
        if (isActive) {
          if (pendingExpand) {
            pendingExpand = false;
            hasAppliedInitialDesktopState = true;
            setTimeout(() => {
              if (teaser.parentElement?.classList.contains('swiper-slide-active')) {
                expand();
              }
            }, CARD_OPEN_DELAY_MS);
          }
        } else {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
          if (teaser.classList.contains('expanded')) {
            collapse();
          } else if (!teaser.classList.contains('is-closing')) {
            clearExpandAnimation();
            clearCollapseAnimation();
            teaser.classList.remove('expanded', 'expanded-settled');
          }
          pendingExpand = false;
        }
      });
      observer.observe(teaser.parentElement, { attributes: true, attributeFilter: ['class'] });
    }
  } else if (
    teaser.classList.contains('cmp-teaser__stripe') ||
    teaser.classList.contains('cmp-teaser__stripe-right')
  ) {
    return;
  } else {
    const button = teaser.querySelector<HTMLElement>('.cmp-teaser__button-wrapper');
    button?.addEventListener('click', () => {
      teaser.classList.toggle('expanded');
    });
    if (teaser.parentElement) {
      const observer = new MutationObserver(() => {
        if (
          !teaser.parentElement?.classList.contains('swiper-slide-active') &&
          teaser.classList.contains('expanded')
        ) {
          teaser.classList.remove('expanded');
        }
      });
      if (teaser.parentElement?.classList.contains('swiper-slide')) {
        observer.observe(teaser.parentElement, { attributes: true, attributeFilter: ['class'] });
      }
    }
  }
};

export const setupExpandingTeasers = (teasers: HTMLElement[]): void => {
  teasers.forEach((teaser) => {
    addEventListenerToButton(teaser);
  });
};
